import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';
import PublicSEO from '../../components/public/PublicSEO';
import { decodeHtml, stripHtml, stripHtmlForMeta } from '../../utils/html';
import { buildBlogPosting, buildBreadcrumb } from '../../utils/jsonLd';
import { getSnapApiData } from '../../utils/snapStore';
import { publicPath } from '../../utils/canonicalUrl';
import './BlogPostPage.css';
import './BlogPublicScope.css';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [articolo, setArticolo] = useState(() => {
    if (!slug) return null;
    return (
      getSnapApiData(`/public/articoli/${encodeURIComponent(slug)}`)
      ?? getSnapApiData(`/public/articoli/${slug}`)
      ?? null
    );
  });
  const [loading, setLoading] = useState(() => {
    if (!slug) return true;
    return !(
      getSnapApiData(`/public/articoli/${encodeURIComponent(slug)}`)
      ?? getSnapApiData(`/public/articoli/${slug}`)
    );
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) return;
    const hasSnap = !!(
      getSnapApiData(`/public/articoli/${encodeURIComponent(slug)}`)
      ?? getSnapApiData(`/public/articoli/${slug}`)
    );
    const load = async () => {
      if (!hasSnap) setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/public/articoli/${encodeURIComponent(slug)}`);
        setArticolo(res.data);
      } catch (err) {
        if (err.response?.status === 404) setArticolo(null);
        else setError(err.response?.data?.message || 'Articolo non trovato.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
  };


  if (loading && !articolo) {
    return (
      <div className="blog-post-page vc-blog-surface">
        <PublicSEO title="Articolo" canonicalPath={`/blog/${slug}`} noIndex />
        <div className="blog-post-container blog-post-loading-shell" aria-busy="true" aria-label="Caricamento articolo" />
      </div>
    );
  }

  if (error || (!articolo && !loading)) {
    return (
      <div className="blog-post-page vc-blog-surface">
        <PublicSEO title="Articolo non trovato" canonicalPath="/blog" noIndex />
        <div className="blog-post-container">
          <p className="blog-post-error">{error || 'Articolo non trovato.'}</p>
          <Link to={publicPath('/blog')} className="vc-btn vc-btn-primary">Torna agli argomenti</Link>
        </div>
      </div>
    );
  }

  const title = articolo.titolo || 'Articolo';
  const slugOrId = articolo.slug || articolo.id;
  const metaTitle = articolo.metaTitle || `${title} | Approfondimenti Virtual Clinic`;
  const description = stripHtmlForMeta(
    articolo.metaDescription
      || articolo.intro
      || articolo.testo
      || `Leggi l'articolo: ${title}`
  );
  const tagsFromArticle = (() => {
    if (Array.isArray(articolo.tags)) return articolo.tags;
    if (typeof articolo.tags === 'string') {
      try {
        const parsed = JSON.parse(articolo.tags);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return articolo.tags.split(',').map((t) => t.trim()).filter(Boolean);
      }
    }
    return [];
  })();
  const customStructuredData = (() => {
    if (!articolo.structuredData) return null;
    if (typeof articolo.structuredData === 'object') return articolo.structuredData;
    try {
      return JSON.parse(articolo.structuredData);
    } catch {
      return null;
    }
  })();

  const jsonLd = [
    buildBlogPosting({
      ...articolo,
      tags: tagsFromArticle,
    }),
    buildBreadcrumb([
      { name: 'Home', path: '/' },
      { name: 'Argomenti', path: '/blog' },
      { name: title },
    ]),
    ...(customStructuredData
      ? (Array.isArray(customStructuredData) ? customStructuredData : [customStructuredData])
      : []),
  ];

  return (
    <div className="blog-post-page vc-blog-surface">
      <PublicSEO
        title={metaTitle}
        description={description}
        canonicalPath={`/blog/${slugOrId}`}
        ogImage={articolo.urlImg}
        ogImageAlt={articolo.coverImageAlt || title}
        ogType="article"
        keywords={articolo.metaKeywords || (tagsFromArticle.length > 0 ? tagsFromArticle.join(', ') : undefined)}
        article={{
          publishedTime: articolo.publishedAt || articolo.dataCreazione || undefined,
          modifiedTime: articolo.lastUpdate || articolo.publishedAt || articolo.dataCreazione || undefined,
          author: 'Virtual Clinic',
          section: articolo.categoria || undefined,
          tags: tagsFromArticle,
        }}
        jsonLd={jsonLd}
      />
      <article className="blog-post-container">
        <nav className="blog-post-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span className="blog-post-breadcrumb-sep" aria-hidden>/</span>
          <Link to={publicPath('/blog')}>Argomenti</Link>
          <span className="blog-post-breadcrumb-sep" aria-hidden>/</span>
          <span className="blog-post-breadcrumb-current">{title}</span>
        </nav>

        <header className="blog-post-header">
          <div className="blog-post-meta">
            <span className="blog-post-author">A cura della <strong>Redazione Virtual Clinic</strong></span>
            {articolo.dataCreazione && (
              <>
                <span className="blog-post-meta-sep" aria-hidden>·</span>
                <span>
                  Pubblicato il{' '}
                  <time dateTime={articolo.dataCreazione}>{formatDate(articolo.dataCreazione)}</time>
                </span>
              </>
            )}
            {articolo.lastUpdate && articolo.lastUpdate !== articolo.dataCreazione && (
              <>
                <span className="blog-post-meta-sep" aria-hidden>·</span>
                <span>
                  Aggiornato il{' '}
                  <time dateTime={articolo.lastUpdate}>{formatDate(articolo.lastUpdate)}</time>
                </span>
              </>
            )}
          </div>
          <h1 className="blog-post-title">{title}</h1>
          {articolo.intro && <p className="blog-post-intro">{stripHtml(articolo.intro)}</p>}
        </header>

        {articolo.urlImg && (
          <div className="blog-post-hero">
            <img src={articolo.urlImg} alt={articolo.coverImageAlt || title} />
          </div>
        )}

        {articolo.testo && (
          <div
            className="blog-post-content blog-post-content-html"
            dangerouslySetInnerHTML={{ __html: decodeHtml(articolo.testo) }}
          />
        )}

        <footer className="blog-post-footer">
          <aside className="blog-post-disclaimer" role="note" aria-label="Avvertenze">
            <p>
              <strong>Avvertenza medica.</strong> Le informazioni contenute in questo articolo
              hanno esclusivamente scopo divulgativo e informativo e non costituiscono in alcun
              modo un parere medico né sostituiscono il consulto, la diagnosi o la prescrizione
              di un professionista sanitario qualificato. In presenza di sintomi o dubbi clinici
              consulta sempre il tuo medico curante o uno specialista. Per prenotare un consulto
              online{' '}
              <Link to={publicPath('/medici')}>scegli uno specialista su Virtual Clinic</Link>.
            </p>
            <p>
              <strong>Trasparenza AI (AI Act).</strong> Questo articolo è stato interamente
              generato tramite sistemi di Intelligenza Artificiale (AI) e revisionato dal team
              editoriale di Virtual Clinic per scopi puramente informativi.
            </p>
          </aside>
          <div className="blog-post-actions">
            <Link to={publicPath('/blog')} className="vc-btn vc-btn-outline">Torna agli argomenti</Link>
          </div>
        </footer>
      </article>
    </div>
  );
};

export default BlogPostPage;
